import React, { useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, CircularProgress } from '@mui/material'
import { Collapse, IconButton, useTheme } from '@mui/material'
import {
    CheckCircleRounded,
    ChevronRight,
    RadioButtonUncheckedRounded,
} from '@mui/icons-material'
import { capitalize } from '../../../../../../utils/functions'
import { courseTopicApiSlice } from '../../../../../../features/course/courseTopicSlice'
import { courseSubjectPriceApi } from '../../../../../../features/course/courseSubjectPrice'
import { PurchasedCourseTopicState } from '../../../../../../features/types'
import { SelectedSubjectState } from '../../types'
import { discount, discountPrice, flexCenter, price } from '../../style'
import SelectTopicSubject from './SelectTopicSubject'

interface Props {
    selectedLanguage: string
    promotionDiscount: number
    coursePriceId: number
    subject: SelectedSubjectState
    queryParamsExists: boolean
    queryCourseSubjectId: number
    onTopicClick: (
        topic: PurchasedCourseTopicState,
        subjectId: number,
        topics?: PurchasedCourseTopicState[],
    ) => void
    onSelectTopics: (
        topics: PurchasedCourseTopicState[],
        subjectId: number,
    ) => void
    handleToggleAllTopics: (subjectId: number) => void
}

const SelectTopicCard: React.FC<Props> = ({
    selectedLanguage,
    promotionDiscount,
    subject,
    coursePriceId,
    queryParamsExists,
    queryCourseSubjectId,
    onTopicClick,
    onSelectTopics,
    handleToggleAllTopics,
}) => {
    const theme = useTheme()
    const { locale } = useIntl()
    const [expanded, setExpanded] = useState(
        subject.courseSubjectId === queryCourseSubjectId,
    )
    const skipQueries =
        !expanded && subject.courseSubjectId !== queryCourseSubjectId
    const { data: topics = [], isFetching: isFetchingTopics } =
        courseTopicApiSlice.useGetPurchaseCourseTopicsQuery(
            {
                courseSubjectId: subject.courseSubjectId,
                coursePriceId,
                language: selectedLanguage,
            },
            {
                skip: skipQueries,
            },
        )
    const { data: courseSubjectsPrices = [] } =
        courseSubjectPriceApi.useGetCourseSubjectsPricesByCoursePriceQuery(
            coursePriceId,
            {
                skip: skipQueries,
            },
        )

    const styles = {
        wrapper: {
            border: `1px solid ${theme.palette.grey[100]}`,
            borderRadius: '8px',
            mb: 2,
            minWidth: '500px',
            width: '100%',

            '@media screen and (max-width: 500px)': {
                minWidth: 'auto',
            },
        },
        container: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            background: theme.palette.grey[100],
            cursor: 'pointer',
            p: 1,
            pr: 2,
        },
        chevron: {
            transform: expanded ? 'rotate(90deg)' : 'rotate(0)',
            transition: '0.15s ease-in-out',
        },
    }

    const getTopicPrice = () => {
        const courseSubjectPrice = courseSubjectsPrices.find(
            (el) => el.courseSubjectId === subject.courseSubjectId,
        )

        if (courseSubjectPrice) {
            return courseSubjectPrice.singleTopicPrice
        }
    }

    useEffect(() => {
        const topicsExists = topics.length
        if (topicsExists && (subject.isSelected || queryParamsExists)) {
            onSelectTopics(topics, subject.id)
        }
    }, [topics])

    return (
        <Box sx={styles.wrapper}>
            <Box sx={styles.container}>
                <Box sx={flexCenter}>
                    <IconButton onClick={() => setExpanded(!expanded)}>
                        <ChevronRight sx={styles.chevron} />
                    </IconButton>

                    <Box ml={1}>{subject[`title${capitalize(locale)}`]}</Box>

                    {subject.courseSubjectPrice.discount > 0 && (
                        <Box sx={discount}>
                            -{subject.courseSubjectPrice.discount}%
                        </Box>
                    )}
                </Box>

                <Box sx={flexCenter}>
                    <Box sx={{ mr: 2 }}>
                        {subject.courseSubjectPrice.discount > 0 && (
                            <>
                                <span style={price}>
                                    {subject.courseSubjectPrice.price} т
                                </span>

                                <span style={{ color: '#9C9DA1' }}> | </span>
                            </>
                        )}

                        <span style={discountPrice}>
                            {subject.courseSubjectPrice.discountPrice} т
                        </span>
                    </Box>

                    {subject.isSelected ? (
                        <CheckCircleRounded
                            sx={{ color: '#0B1C3F' }}
                            onClick={() => handleToggleAllTopics(subject.id)}
                        />
                    ) : (
                        <RadioButtonUncheckedRounded
                            onClick={() => handleToggleAllTopics(subject.id)}
                        />
                    )}
                </Box>
            </Box>

            <Collapse in={expanded}>
                {isFetchingTopics ? (
                    <CircularProgress sx={{ m: 1 }} />
                ) : (
                    topics.map((topic, index) => (
                        <SelectTopicSubject
                            key={topic.courseTopicId}
                            selected={subject.selectedTopics?.some(
                                (selected) =>
                                    selected.courseTopicId ===
                                    topic.courseTopicId,
                            )}
                            promotionDiscount={promotionDiscount}
                            subject={subject}
                            queryParamsExists={queryParamsExists}
                            onTopicClick={onTopicClick}
                            singleTopicPrice={getTopicPrice()}
                            topics={topics}
                            topic={topic}
                            index={index}
                        />
                    ))
                )}
            </Collapse>
        </Box>
    )
}

export default SelectTopicCard
